// The single source of truth for the terminal workspace: tabs + a recursive
// split-pane tree where each pane carries its own host. Serializable and synced
// to the backend (terminal_workspace) for cross-device restore. Live xterm/WS
// handles live in terminalManager, NOT here.

import { create } from 'zustand';
import { persist } from 'zustand/middleware';

import { terminalApi } from '../lib/api';
import {
    collectPanes,
    findPane,
    insertBeside,
    localId,
    paneNode,
    removeFromTree,
    setSizes as setSizesIn,
    splitTree,
    swapPanes,
} from '../lib/layoutTree';
import type {
    LayoutNode,
    PaneDropZone,
    PaneKind,
    PaneNode,
    TermTab,
    Workspace,
} from '../types/terminal';

// Transient request from outside the workspace (sidebar click, search palette,
// session manager) to focus a host or open a specific session. Not persisted —
// the workspace component consumes it once it has finished booting.
interface PendingFocus {
    hostId: string;
    sessionId?: string;
    label?: string;
    /** 'sftp' asks for the host's file browser instead of a terminal. */
    kind?: PaneKind;
    n: number; // bumped each request so repeated asks re-trigger
}

interface WorkspaceStore {
    workspace: Workspace | null;
    pendingFocus: PendingFocus | null;
    replaceWorkspace: (ws: Workspace | null) => void;
    requestFocusHost: (hostId: string) => void;
    requestOpenSession: (hostId: string, sessionId: string, label: string) => void;
    requestOpenSftp: (hostId: string) => void;
    clearPendingFocus: () => void;
    addTab: (
        hostId: string,
        sessionId: string,
        title: string,
        kind?: PaneKind,
    ) => void;
    closeTab: (tabId: string) => void;
    splitPane: (
        targetSessionId: string,
        dir: 'row' | 'col',
        newSessionId: string,
        newHostId: string,
        kind?: PaneKind,
    ) => void;
    closePane: (sessionId: string) => void;
    movePane: (
        draggedSessionId: string,
        targetSessionId: string,
        zone: PaneDropZone,
    ) => void;
    setSizes: (splitId: string, sizes: [number, number]) => void;
    setActiveTab: (tabId: string) => void;
    setActivePane: (sessionId: string) => void;
    renameTab: (tabId: string, title: string) => void;
}

const empty = (): Workspace => ({ tabs: [], activeTabId: '', activePaneId: '' });

// Pick a sane active tab/pane after the tree changes.
function recompute(
    tabs: TermTab[],
    prevTabId: string,
    prevPaneId: string,
): { activeTabId: string; activePaneId: string } {
    const first = tabs[0];
    if (!first) return { activeTabId: '', activePaneId: '' };
    const activeTabId = tabs.some((t) => t.id === prevTabId)
        ? prevTabId
        : first.id;
    const tab = tabs.find((t) => t.id === activeTabId) ?? first;
    const panes = collectPanes(tab.layout);
    const activePaneId = panes.some((p) => p.id === prevPaneId)
        ? prevPaneId
        : (panes[0]?.id ?? '');
    return { activeTabId, activePaneId };
}

const mapTab = (
    ws: Workspace,
    tabId: string,
    fn: (layout: LayoutNode) => LayoutNode,
): TermTab[] =>
    ws.tabs.map((t) => (t.id === tabId ? { ...t, layout: fn(t.layout) } : t));

export const useTerminalWorkspace = create<WorkspaceStore>()(
    persist(
        (set) => ({
            workspace: null,
            pendingFocus: null,

            replaceWorkspace: (ws) => set({ workspace: ws }),

            requestFocusHost: (hostId) =>
                set((s) => ({
                    pendingFocus: { hostId, n: (s.pendingFocus?.n ?? 0) + 1 },
                })),
            requestOpenSession: (hostId, sessionId, label) =>
                set((s) => ({
                    pendingFocus: {
                        hostId,
                        sessionId,
                        label,
                        n: (s.pendingFocus?.n ?? 0) + 1,
                    },
                })),
            requestOpenSftp: (hostId) =>
                set((s) => ({
                    pendingFocus: {
                        hostId,
                        kind: 'sftp',
                        n: (s.pendingFocus?.n ?? 0) + 1,
                    },
                })),
            clearPendingFocus: () => set({ pendingFocus: null }),

            addTab: (hostId, sessionId, title, kind) =>
                set((s) => {
                    const ws = s.workspace ?? empty();
                    const tab: TermTab = {
                        id: localId('t'),
                        title,
                        layout: paneNode(sessionId, hostId, kind),
                    };
                    return {
                        workspace: {
                            tabs: [...ws.tabs, tab],
                            activeTabId: tab.id,
                            activePaneId: sessionId,
                        },
                    };
                }),

            closeTab: (tabId) =>
                set((s) => {
                    if (!s.workspace) return s;
                    const tabs = s.workspace.tabs.filter((t) => t.id !== tabId);
                    return {
                        workspace: {
                            tabs,
                            ...recompute(
                                tabs,
                                s.workspace.activeTabId,
                                s.workspace.activePaneId,
                            ),
                        },
                    };
                }),

            splitPane: (targetSessionId, dir, newSessionId, newHostId, kind) =>
                set((s) => {
                    if (!s.workspace) return s;
                    const target = s.workspace.tabs.find((t) =>
                        findPane(t.layout, targetSessionId),
                    );
                    if (!target) return s;
                    const tabs = mapTab(s.workspace, target.id, (layout) =>
                        splitTree(
                            layout,
                            targetSessionId,
                            dir,
                            paneNode(newSessionId, newHostId, kind),
                        ),
                    );
                    return {
                        workspace: {
                            ...s.workspace,
                            tabs,
                            activeTabId: target.id,
                            activePaneId: newSessionId,
                        },
                    };
                }),

            closePane: (sessionId) =>
                set((s) => {
                    if (!s.workspace) return s;
                    const owner = s.workspace.tabs.find((t) =>
                        findPane(t.layout, sessionId),
                    );
                    if (!owner) return s;
                    const nextLayout = removeFromTree(owner.layout, sessionId);
                    const tabs = nextLayout
                        ? s.workspace.tabs.map((t) =>
                              t.id === owner.id ? { ...t, layout: nextLayout } : t,
                          )
                        : s.workspace.tabs.filter((t) => t.id !== owner.id);
                    return {
                        workspace: {
                            tabs,
                            ...recompute(
                                tabs,
                                s.workspace.activeTabId,
                                s.workspace.activePaneId,
                            ),
                        },
                    };
                }),

            // Drag-and-drop a pane onto another: the four edges split the target
            // (placing the dragged pane on that side), `center` swaps the two.
            movePane: (draggedSessionId, targetSessionId, zone) =>
                set((s) => {
                    if (!s.workspace || draggedSessionId === targetSessionId)
                        return s;
                    const ws = s.workspace;
                    const dragged = ws.tabs
                        .map((t) => findPane(t.layout, draggedSessionId))
                        .find((p): p is PaneNode => p !== null);
                    const targetTab = ws.tabs.find((t) =>
                        findPane(t.layout, targetSessionId),
                    );
                    if (!dragged || !targetTab) return s;

                    // Center → swap the two panes in place.
                    if (zone === 'center') {
                        const target = findPane(targetTab.layout, targetSessionId);
                        if (!target) return s;
                        const tabs = ws.tabs.map((t) => ({
                            ...t,
                            layout: swapPanes(t.layout, dragged, target),
                        }));
                        return {
                            workspace: { ...ws, tabs, activePaneId: draggedSessionId },
                        };
                    }

                    const dir: 'row' | 'col' =
                        zone === 'left' || zone === 'right' ? 'row' : 'col';
                    const before = zone === 'left' || zone === 'top';

                    // 1) Pull the dragged pane out of wherever it lives (dropping
                    //    any tab it leaves empty).
                    const pruned = ws.tabs
                        .map((t) => {
                            const layout = removeFromTree(t.layout, draggedSessionId);
                            return layout ? { ...t, layout } : null;
                        })
                        .filter((t): t is TermTab => t !== null);

                    // 2) Re-insert it beside the target in the target's tab.
                    const tabs = pruned.map((t) =>
                        t.id === targetTab.id
                            ? {
                                  ...t,
                                  layout: insertBeside(
                                      t.layout,
                                      targetSessionId,
                                      dir,
                                      dragged,
                                      before,
                                  ),
                              }
                            : t,
                    );

                    return {
                        workspace: {
                            tabs,
                            ...recompute(tabs, targetTab.id, draggedSessionId),
                        },
                    };
                }),

            setSizes: (splitId, sizes) =>
                set((s) => {
                    if (!s.workspace) return s;
                    return {
                        workspace: {
                            ...s.workspace,
                            tabs: s.workspace.tabs.map((t) => ({
                                ...t,
                                layout: setSizesIn(t.layout, splitId, sizes),
                            })),
                        },
                    };
                }),

            setActiveTab: (tabId) =>
                set((s) => {
                    if (!s.workspace) return s;
                    const tab = s.workspace.tabs.find((t) => t.id === tabId);
                    if (!tab) return s;
                    const panes = collectPanes(tab.layout);
                    return {
                        workspace: {
                            ...s.workspace,
                            activeTabId: tabId,
                            activePaneId: panes.some(
                                (p) => p.id === s.workspace!.activePaneId,
                            )
                                ? s.workspace.activePaneId
                                : (panes[0]?.id ?? ''),
                        },
                    };
                }),

            setActivePane: (sessionId) =>
                set((s) =>
                    s.workspace
                        ? { workspace: { ...s.workspace, activePaneId: sessionId } }
                        : s,
                ),

            renameTab: (tabId, title) =>
                set((s) =>
                    s.workspace
                        ? {
                              workspace: {
                                  ...s.workspace,
                                  tabs: s.workspace.tabs.map((t) =>
                                      t.id === tabId ? { ...t, title } : t,
                                  ),
                              },
                          }
                        : s,
                ),
        }),
        {
            name: 'hbc-terminal-workspace',
            partialize: (s) => ({ workspace: s.workspace }),
        },
    ),
);

// ─── Debounced server sync ──────────────────────────────────────────────────
// Disabled during the initial server load/reconcile so we don't echo the
// freshly-fetched layout straight back. The workspace component enables it
// after reconciling.
let syncEnabled = false;
let timer: ReturnType<typeof setTimeout> | null = null;

export function setWorkspaceSyncEnabled(on: boolean): void {
    syncEnabled = on;
    // Disabling cancels any save already queued, so a deliberate local clear
    // (e.g. sign-out) can't echo an empty layout to the server.
    if (!on && timer) {
        clearTimeout(timer);
        timer = null;
    }
}

/**
 * Sign-out cleanup: drop the in-memory + cached workspace WITHOUT syncing the
 * empty layout to the server, so the saved layout is restored on next login.
 * Sync is re-enabled by the workspace component the next time it boots.
 */
export function clearWorkspaceForSignOut(): void {
    setWorkspaceSyncEnabled(false);
    useTerminalWorkspace.getState().replaceWorkspace(null);
}

useTerminalWorkspace.subscribe((state, prev) => {
    if (!syncEnabled) return;
    if (state.workspace === prev.workspace) return;
    if (!state.workspace) return;
    if (timer) clearTimeout(timer);
    const snapshot = state.workspace;
    timer = setTimeout(() => {
        void terminalApi.saveWorkspace(snapshot).catch((err) => {
            console.warn('workspace sync failed', err);
        });
    }, 500);
});
